import type { MonthBounds } from './months';
import { monthKeyOf } from './months';

// ─── Types ──────────────────────────────────────────────────────────────

export type ServicePack = { name: string; date: string }; // ISO "YYYY-MM-DD"

export type ReleaseCal = {
  start?: string;   // ISO date
  end?: string;     // ISO date
  sps?: ServicePack[];
};

export type ReleasePhase = {
  start: Date;
  end: Date;
  name: string;
  colorIdx: number;
  isFirst?: boolean;
  isLast?: boolean;
  isFinal?: boolean;
};

export type Span = { leftPct: number; widthPct: number };

// ─── Helpers ────────────────────────────────────────────────────────────

const MS_PER_DAY = 86400000;

// Berechnet den horizontalen Span [from, to] innerhalb der Monatsgrenzen
// in Prozent. Liefert null, wenn kein Overlap.
export function spanInMonth(from: Date, to: Date, mb: MonthBounds): Span | null {
  if (to < mb.start || from > mb.end) return null;
  const s = from < mb.start ? mb.start : from;
  const e = to   > mb.end   ? mb.end   : to;
  const leftPct  = ((s.getTime() - mb.start.getTime()) / MS_PER_DAY) / mb.totalDays * 100;
  const widthPct = ((e.getTime() - s.getTime())        / MS_PER_DAY + 1) / mb.totalDays * 100;
  return { leftPct, widthPct };
}

// Zerlegt einen Release in Phasen entlang seiner Service Packs.
// Letzte Phase ist "Final" (vom letzten SP bis zum Release-Ende).
// Ohne SPs: leere Liste (es wird nur das Haupt-Band gerendert).
export function computeReleasePhases(relCal: ReleaseCal | undefined): ReleasePhase[] {
  if (!relCal?.start || !relCal?.end) return [];
  const start = new Date(relCal.start);
  const end   = new Date(relCal.end);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return [];

  const sps = (relCal.sps ?? [])
    .filter(sp => sp.name && sp.date)
    .map(sp => ({ name: sp.name, date: new Date(sp.date) }))
    .filter(sp => !isNaN(sp.date.getTime()) && sp.date >= start && sp.date <= end)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  if (sps.length === 0) return [];

  const phases: ReleasePhase[] = [];
  let prev = new Date(start);
  for (const sp of sps) {
    if (sp.date < prev) continue;
    phases.push({
      start: new Date(prev),
      end: new Date(sp.date),
      name: sp.name,
      colorIdx: phases.length,
    });
    prev = new Date(sp.date);
    prev.setDate(prev.getDate() + 1);
  }
  if (prev <= end) {
    phases.push({
      start: new Date(prev),
      end: new Date(end),
      name: 'Final',
      colorIdx: phases.length,
      isFinal: true,
    });
  }
  if (phases.length) {
    phases[0].isFirst = true;
    phases[phases.length - 1].isLast = true;
  }
  return phases;
}

// Sortiert Releases nach Start-Datum (frühestes zuerst). Releases ohne
// Start-Datum landen ans Ende.
export function sortReleasesByStart(cal: Record<string, ReleaseCal>): string[] {
  return Object.keys(cal)
    .filter(name => cal[name]?.start && cal[name]?.end)
    .sort((a, b) => (cal[a].start ?? '').localeCompare(cal[b].start ?? ''));
}

// Default-Release-Kalender mit den vom User vorgegebenen Hard-Codierten
// Releases (2026 R2, 2027 R1, 2027 R2). Wird beim Laden in den Store gemergt,
// sofern noch nicht gepflegt.
export function defaultReleaseCal(): Record<string, ReleaseCal> {
  return {
    '2026 R2': {
      start: '2026-04-01',
      end:   '2026-09-30',
      sps: [
        { name: 'SP1', date: '2026-05-15' },
        { name: 'SP2', date: '2026-06-30' },
        { name: 'SP3', date: '2026-09-30' },
      ],
    },
    '2027 R1': {
      start: '2026-10-01',
      end:   '2027-03-31',
      sps: [],
    },
    '2027 R2': {
      start: '2027-04-01',
      end:   '2027-09-30',
      sps: [],
    },
  };
}

// Color-Palette für Releases — pro Lane eine eigene Farbe (zyklisch).
// Werte aus V1 übernommen, leicht M3-tauglich gemacht.
export const RELEASE_COLORS = [
  '#8250df', // violet
  '#1f6feb', // blue
  '#1a7f37', // green
  '#d97706', // amber
  '#0d9488', // teal
  '#bf3989', // magenta
];

export function colorForLane(idx: number): string {
  return RELEASE_COLORS[idx % RELEASE_COLORS.length];
}

// Sub-Color-Palette für SP-Phasen (innerhalb eines Release-Bands).
// Final-Phase = grau-grün.
export const PHASE_COLORS = ['#7c3aed', '#2563eb', '#0891b2', '#ea580c', '#dc2626'];
export const FINAL_PHASE_COLOR = '#6b7280';

export function colorForPhase(p: ReleasePhase): string {
  if (p.isFinal) return FINAL_PHASE_COLOR;
  return PHASE_COLORS[p.colorIdx % PHASE_COLORS.length];
}

// Created/Resolved-Datumsfelder erkennen — heißen je nach JIRA-Export
// "Created" / "Resolved" oder mit anderen Großschreibungen.
export function dateMonthKey(s: unknown): string | null {
  if (typeof s !== 'string' || !s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return monthKeyOf(d);
}
